import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { FileSchema } from "../../schemas/file.js";
export type { FileSource } from "../../schemas/file.js";

const EntityTypeSchema = z.enum(["client", "resource", "task"]);
export type FileEntityType = z.infer<typeof EntityTypeSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: EntityTypeSchema
});

const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

const ResponseSchema = z.array(FileSchema);

const PaginatedFileSchema = FileSchema.extend({
  // /file/list exposes the BoardUser id, while the legacy entity endpoints
  // expose the display name. File cards do not display this value today.
  creator: z.union([z.string(), z.number()]).nullable()
}).transform(({ creator, ...file }) => ({
  ...file,
  creator: typeof creator === "string" ? creator : null
}));

const PaginatedResponseSchema = z.object({
  count: z.number().nonnegative(),
  results: z.array(PaginatedFileSchema)
});

const PaginatedRequestSchema = z.object({
  board_id: z.int().positive(),
  task: z.int().positive().optional(),
  resource: z.int().positive().optional(),
  client: z.int().positive().optional(),
  search: z.string().optional(),
  ordering: z.enum([
    "created_at",
    "-created_at",
    "updated_at",
    "-updated_at",
    "order",
    "-order",
    "id",
    "-id"
  ]),
  limit: z.int().min(1).max(200),
  offset: z.int().nonnegative()
});

type Response = z.infer<typeof ResponseSchema>;
type Request = z.infer<typeof RequestSchema>;

export type EntityFilesResponse = Response;
export type EntityFilesRequest = Request;
export type EntityFilesPage = z.infer<typeof PaginatedResponseSchema>;

export type EntityFilesInfiniteRequest = {
  id: number;
  board_id: number;
  entity_type: FileEntityType;
  search?: string;
};

export type EntityFilesProps = Request;

const urls = {
  wf: {
    task: "/task/:id/files",
    resource: "/resource/:id/files",
    client: "/client/:id/files"
  },
  public: {
    task: "/public/:slug/task/:id/files",
    resource: "/public/:slug/resource/:id/files",
    client: "/public/:slug/client/:id/files"
  }
} as const;

const fetcher = async (body: Request) => {
  const { api: apiType, ...rest } = body;
  return api<Omit<Request, "api">, Response>({
    method: "GET",
    url: urls[apiType][rest.entity_type],
    requestSchema: RequestVariables,
    responseSchema: ResponseSchema,
    body: rest
  });
};

export const useEntityFiles = (entity_type: FileEntityType) =>
  createQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}FilesWithSuspense`],
    fetcher
  });

export const useEntityFilesSuspense = (entity_type: FileEntityType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}FilesWithSuspense`],
    fetcher
  });

const paginatedFetcher = ({
  id,
  entity_type,
  ...params
}: EntityFilesInfiniteRequest & { limit: number; offset: number }) =>
  api<z.infer<typeof PaginatedRequestSchema>, EntityFilesPage>({
    method: "GET",
    url: "/file/list",
    requestSchema: PaginatedRequestSchema,
    responseSchema: PaginatedResponseSchema,
    body: {
      ...params,
      [entity_type]: id,
      ordering: "-created_at"
    }
  });

type UseEntityFilesInfiniteOptions = {
  variables: EntityFilesInfiniteRequest;
  pageSize?: number;
};

export function useEntityFilesInfinite({
  variables,
  pageSize = 30
}: UseEntityFilesInfiniteOptions) {
  return useSuspenseInfiniteQuery({
    queryKey: [
      `entity${variables.entity_type}FilesWithSuspense`,
      "infinite",
      variables,
      pageSize
    ] as const,
    queryFn: ({ pageParam }) =>
      paginatedFetcher({
        ...variables,
        limit: pageSize,
        offset: pageParam
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const nextOffset = lastPageParam + lastPage.results.length;
      return nextOffset < lastPage.count ? nextOffset : undefined;
    }
  });
}
